import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Flex, ListItem, Text, TextButton } from '@modusign/design-system';
// eslint-disable-next-line import/no-unresolved
import * as CSS from 'csstype';
import { ReactNode } from 'react';

import {
  WindowModal,
  WindowModalContent,
  WindowModalHeader,
} from '@app/components/Modal/WindowModal';

interface ExcelValidationErrorModalProps {
  errors: Array<{ cell: string; message: ReactNode; columnName: string }>;
  isOpen: boolean;
  sideBarContent?: ReactNode;
  sideBarMinWidth?: CSS.Property.MinWidth;
  onClose: () => void;
  onRetry?: () => void;
}

export default function ExcelValidationErrorModal({
  errors,
  isOpen,
  sideBarContent = <DefaultSideBarContent />,
  sideBarMinWidth = '200px',
  onClose,
  onRetry,
}: ExcelValidationErrorModalProps) {
  return (
    <WindowModal isOpen={isOpen} maxWidth="md" onRequestClose={onClose}>
      <WindowModalHeader sx={{ color: 'textNeutralLight', fontWeight: 'bold' }} onClose={onClose}>
        <Box as="span" mr={2}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </Box>
        엑셀 입력 오류
      </WindowModalHeader>
      <WindowModalContent height="480px" px={0} py={0}>
        <Flex alignItems="stretch" height="100%">
          <Flex
            borderRight="1px solid"
            borderRightColor="boxBorderNeutralBase"
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
            minWidth={sideBarMinWidth}
            px={6}
            py={7}
          >
            <Box>{sideBarContent}</Box>
            {onRetry && (
              <Box textAlign="center">
                <Button onClick={onRetry}>엑셀 파일 재업로드</Button>
              </Box>
            )}
          </Flex>
          <Box backgroundColor="lightGray50" height="100%" overflowY="auto" p={7} width="100%">
            <Text as="p" color="textNeutralLight" fontSize="sm" fontWeight="medium" mb={4}>
              오류 상세
            </Text>
            {errors.map(({ cell, message, columnName }) => (
              <ErrorListItem cell={cell} columnName={columnName} key={cell} message={message} />
            ))}
          </Box>
        </Flex>
      </WindowModalContent>
    </WindowModal>
  );
}

interface ErrorListItemProps {
  cell: string;
  columnName: string;
  message: ReactNode;
}

function ErrorListItem({ cell, columnName, message }: ErrorListItemProps) {
  return (
    <Box
      as="li"
      bg="white"
      border="1px solid"
      borderColor="boxBorderNeutralBase"
      borderRadius="4px"
      boxShadow="0px 2px 8px rgba(0, 0, 0, 0.05)"
      mb={4}
      px={5}
      py={4}
    >
      <Text as="p">
        <Text color="textNegativeBase" fontWeight="bold">
          {cell}
        </Text>
        <Text fontWeight="bold" ml={1}>
          {columnName}
        </Text>
      </Text>
      <Text as="p" color="textNeutralLight" fontSize="sm" mt={1}>
        {message}
      </Text>
    </Box>
  );
}

function DefaultSideBarContent() {
  return (
    <>
      <Text as="p" color="textNeutralLight" fontSize="sm" fontWeight="medium">
        어떻게 하면 되나요?
      </Text>
      <Divider my={4} />
      <ul>
        <ListItem mb={2} variant="square">
          <Text fontSize="sm">
            <Text color="textNegativeBase" fontSize="sm" fontWeight="medium">
              {"'붉은색'"}
            </Text>
            으로 표시된 셀의 입력값을 수정해 주세요.
          </Text>
        </ListItem>
        <ListItem variant="square">
          <Text fontSize="sm">수정한 엑셀 파일을 다시 업로드해 주세요.</Text>
        </ListItem>
      </ul>
      <Divider my={4} />
      <TextButton textDecoration="plain">
        <Box as="span" mr={1}>
          <FontAwesomeIcon icon={faCircleQuestion} />
        </Box>
        도움말
      </TextButton>
    </>
  );
}
